import { useRef, useState } from 'react';
import { C } from '../../theme';
import { api, ApiError } from '../../api';
import type { CreateTenancyPayload, Property, Unit } from '../../api';
import { useAsync } from '../../useAsync';
import { useToast } from '../../components/Toast';
import { Spinner } from '../../components/States';
import {
  Sheet,
  Field,
  FieldRow,
  TextInput,
  MoneyInput,
  SelectInput,
  Segmented,
  ToggleRow,
  Collapsible,
  SectionLabel,
  ErrorBanner,
  PrimaryBtn,
  GhostBtn,
  rupeesToPaise,
  paiseToRupees,
  dateInputToEpoch,
  epochToDateInput,
  pctToBps,
} from './Sheet';

const UNIT_KIND_OPTS = [
  { value: 'room', label: 'Room' },
  { value: 'shop', label: 'Shop' },
  { value: 'office', label: 'Office' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'showroom', label: 'Showroom' },
  { value: 'other', label: 'Other' },
];

const GST_OPTS = [
  { value: 'none', label: 'None' },
  { value: 'add', label: 'Add GST' },
  { value: 'rcm', label: 'RCM' },
];

/**
 * The #1 owner flow — "how do I add a tenant". One sheet that:
 *   1. creates the tenant business (POST /parties),
 *   2. resolves the unit (an existing one, or POST /units to make a new one),
 *   3. creates the tenancy (POST /tenancies) with money → paise, dates → epoch.
 * Advanced tax/escalation toggles stay collapsed (simple-by-default).
 */
export function AddTenancy({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const notify = useToast();

  // Property + existing units for the picker.
  const { data, loading } = useAsync<{ property: Property | null; units: Unit[] }>(async () => {
    const props = await api.properties().catch(() => [] as Property[]);
    const property = props[0] ?? null;
    const units = property ? await api.units(property.id).catch(() => [] as Unit[]) : [];
    return { property, units };
  }, []);
  const property = data?.property ?? null;
  const units = data?.units ?? [];

  // Tenant — an individual (a person renting a room) or a business.
  const [entityType, setEntityType] = useState<'individual' | 'business'>('individual');
  const [displayName, setDisplayName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');

  // Unit
  const [unitMode, setUnitMode] = useState<'existing' | 'new'>('existing');
  const [unitId, setUnitId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newFloor, setNewFloor] = useState('');
  const [newKind, setNewKind] = useState('room');

  // Lease terms
  const [rent, setRent] = useState('');
  const [deposit, setDeposit] = useState('');
  const [dueDay, setDueDay] = useState('5');
  const [startDate, setStartDate] = useState(epochToDateInput(Date.now()));
  const [endDate, setEndDate] = useState('');

  // Advanced toggles (collapsed by default)
  const [advOpen, setAdvOpen] = useState(false);
  const [tdsEnabled, setTdsEnabled] = useState(false);
  const [gstMode, setGstMode] = useState<'none' | 'add' | 'rcm'>('none');
  const [escEnabled, setEscEnabled] = useState(false);
  const [escPct, setEscPct] = useState('5');
  const [escCycle, setEscCycle] = useState('12');
  const [camEnabled, setCamEnabled] = useState(false);
  const [camAmount, setCamAmount] = useState('');
  const [elecEnabled, setElecEnabled] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reuse a party/unit already created on a prior failed attempt (no duplicates on retry).
  const createdPartyId = useRef<string | null>(null);
  const createdUnitId = useRef<string | null>(null);

  const rentPaise = rupeesToPaise(rent);
  const canSubmit =
    displayName.trim() !== '' &&
    rentPaise != null &&
    rentPaise > 0 &&
    (unitMode === 'existing' ? unitId !== '' : newLabel.trim() !== '') &&
    !!property;

  async function submit() {
    setError(null);
    if (!property) {
      setError('No property found for this account.');
      return;
    }
    if (!displayName.trim()) return setError(entityType === 'business' ? 'Enter the tenant business name.' : "Enter the tenant's name.");
    if (rentPaise == null || rentPaise <= 0) return setError('Enter a valid monthly rent.');
    if (unitMode === 'existing' && !unitId) return setError('Pick a unit, or create a new one.');
    if (unitMode === 'new' && !newLabel.trim()) return setError('Enter a label for the new unit.');

    const depositPaise = rupeesToPaise(deposit) ?? 0;
    const camPaise = camEnabled ? rupeesToPaise(camAmount) : null;
    if (camEnabled && (camPaise == null || camPaise < 0)) return setError('Enter a valid CAM amount.');

    setBusy(true);
    try {
      // 1. Tenant business (reuse if a prior attempt already created it).
      let partyId = createdPartyId.current;
      if (!partyId) {
        const isBiz = entityType === 'business';
        const party = await api.createTenant({
          displayName: displayName.trim(),
          entityType,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          legalName: isBiz ? legalName.trim() || undefined : undefined,
          gstin: isBiz ? gstin.trim() || undefined : undefined,
          pan: isBiz ? pan.trim() || undefined : undefined,
        });
        partyId = party.id;
        createdPartyId.current = party.id;
      }

      // 2. Unit — existing selection or a freshly-created one.
      let resolvedUnitId = unitMode === 'existing' ? unitId : createdUnitId.current;
      if (unitMode === 'new' && !resolvedUnitId) {
        const unit = await api.createUnit({
          propertyId: property.id,
          label: newLabel.trim(),
          floor: newFloor.trim() || undefined,
          kind: newKind,
        });
        resolvedUnitId = unit.id;
        createdUnitId.current = unit.id;
      }
      if (!resolvedUnitId) throw new ApiError(400, 'Could not resolve the unit.');

      // 3. Tenancy — money → paise, dates → epoch ms, advanced toggles applied.
      const payload: CreateTenancyPayload = {
        unitId: resolvedUnitId,
        tenantPartyId: partyId,
        status: 'active',
        rentAmount: rentPaise,
        rentDueDay: Number(dueDay) || 5,
        depositAmount: depositPaise,
        startDate: dateInputToEpoch(startDate),
        endDate: dateInputToEpoch(endDate),
        tdsEnabled,
        gstMode,
        escalationEnabled: escEnabled,
        escalationPctBps: escEnabled ? pctToBps(escPct) : null,
        escalationCycleMonths: escEnabled ? Number(escCycle) || 12 : null,
        camEnabled,
        camAmount: camEnabled ? camPaise : null,
        electricityEnabled: elecEnabled,
      };
      await api.createTenancy(payload);

      notify(`${displayName.trim()} added${email.trim() || phone.trim() ? ', tenant can now sign in' : ''}`);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the tenancy. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      icon="key"
      title="Add tenancy"
      subtitle={property ? property.name : 'Loading property…'}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <GhostBtn onClick={onClose} disabled={busy}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} disabled={!canSubmit} busy={busy}>Add tenancy</PrimaryBtn>
        </>
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}><Spinner /></div>
      ) : (
        <>
          <ErrorBanner message={error} />

          {/* Tenant */}
          <SectionLabel>Tenant</SectionLabel>
          <Segmented
            value={entityType}
            onChange={(v) => setEntityType(v as 'individual' | 'business')}
            options={[
              { value: 'individual', label: 'Individual' },
              { value: 'business', label: 'Business' },
            ]}
          />
          <Field label={entityType === 'business' ? 'Business name' : 'Full name'} required>
            <TextInput value={displayName} onChange={setDisplayName} placeholder={entityType === 'business' ? 'e.g. Sequeira Motors' : 'e.g. Rajesh Kumar'} autoFocus />
          </Field>
          <FieldRow>
            <Field label="Email" hint="The tenant signs in with this email or phone.">
              <TextInput value={email} onChange={setEmail} placeholder="tenant@example.com" type="email" inputMode="email" />
            </Field>
            <Field label="Phone">
              <TextInput value={phone} onChange={setPhone} placeholder="+91…" type="tel" inputMode="tel" />
            </Field>
          </FieldRow>
          {entityType === 'business' && (
            <>
              <Field label="Legal name">
                <TextInput value={legalName} onChange={setLegalName} placeholder="Registered name (optional)" />
              </Field>
              <FieldRow>
                <Field label="GSTIN" hint="Often N/A for small tenants.">
                  <TextInput value={gstin} onChange={setGstin} placeholder="Optional" />
                </Field>
                <Field label="PAN">
                  <TextInput value={pan} onChange={setPan} placeholder="Optional" />
                </Field>
              </FieldRow>
            </>
          )}

          {/* Unit */}
          <SectionLabel>Unit</SectionLabel>
          <Segmented
            value={unitMode}
            onChange={(v) => setUnitMode(v as 'existing' | 'new')}
            options={[
              { value: 'existing', label: 'Existing unit' },
              { value: 'new', label: 'New unit' },
            ]}
          />
          {unitMode === 'existing' ? (
            units.length === 0 ? (
              <div style={{ fontSize: 13, color: C.muted }}>
                No units yet. Switch to <b style={{ color: C.ink2 }}>New unit</b> to create one.
              </div>
            ) : (
              <Field label="Pick a room" required hint="Rent pre-fills from the room; you can change it below.">
                <SelectInput
                  value={unitId}
                  onChange={(id) => {
                    setUnitId(id);
                    const u = units.find((x) => x.id === id);
                    if (u?.baseRent != null) setRent(paiseToRupees(u.baseRent));
                  }}
                  options={[
                    { value: '', label: 'Select a room…' },
                    ...units.map((u) => ({
                      value: u.id,
                      label: `${u.label}${u.floor ? ` · ${u.floor}` : ''}${u.baseRent != null ? ` · ₹${paiseToRupees(u.baseRent)}` : ''}`,
                    })),
                  ]}
                />
              </Field>
            )
          ) : (
            <>
              <Field label="Unit label" required>
                <TextInput value={newLabel} onChange={setNewLabel} placeholder="e.g. Unit 6" />
              </Field>
              <FieldRow>
                <Field label="Floor">
                  <TextInput value={newFloor} onChange={setNewFloor} placeholder="e.g. Ground" />
                </Field>
                <Field label="Kind">
                  <SelectInput value={newKind} onChange={setNewKind} options={UNIT_KIND_OPTS} />
                </Field>
              </FieldRow>
            </>
          )}

          {/* Lease terms */}
          <SectionLabel>Lease terms</SectionLabel>
          <FieldRow>
            <Field label="Monthly rent" required>
              <MoneyInput value={rent} onChange={setRent} placeholder="90000" />
            </Field>
            <Field label="Deposit">
              <MoneyInput value={deposit} onChange={setDeposit} placeholder="0" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Rent due day" hint="Day of month (1–28).">
              <TextInput value={dueDay} onChange={(v) => setDueDay(v.replace(/[^0-9]/g, ''))} inputMode="numeric" />
            </Field>
            <Field label="Start date">
              <TextInput value={startDate} onChange={setStartDate} type="date" />
            </Field>
          </FieldRow>
          <Field label="End date" hint="Leave blank for an open-ended tenancy.">
            <TextInput value={endDate} onChange={setEndDate} type="date" />
          </Field>

          {/* Advanced — collapsed by default */}
          <Collapsible open={advOpen} onToggle={() => setAdvOpen((o) => !o)} label={advOpen ? 'Hide advanced' : 'Advanced (TDS, GST, escalation, CAM)'}>
            <ToggleRow
              label="Tenant deducts TDS"
              hint="Net expected drops by the standard 10% the tenant withholds."
              on={tdsEnabled}
              onToggle={setTdsEnabled}
            />
            <Field label="GST treatment">
              <Segmented value={gstMode} onChange={(v) => setGstMode(v as 'none' | 'add' | 'rcm')} options={GST_OPTS} />
            </Field>
            <ToggleRow
              label="Annual escalation"
              hint="Bumps base rent on a fixed cycle."
              on={escEnabled}
              onToggle={setEscEnabled}
            >
              <FieldRow>
                <Field label="Escalation %">
                  <TextInput value={escPct} onChange={(v) => setEscPct(v.replace(/[^0-9.]/g, ''))} inputMode="decimal" />
                </Field>
                <Field label="Every N months">
                  <TextInput value={escCycle} onChange={(v) => setEscCycle(v.replace(/[^0-9]/g, ''))} inputMode="numeric" />
                </Field>
              </FieldRow>
            </ToggleRow>
            <ToggleRow label="CAM charge" hint="Common-area maintenance added each month." on={camEnabled} onToggle={setCamEnabled}>
              <Field label="CAM amount / month">
                <MoneyInput value={camAmount} onChange={setCamAmount} placeholder="0" />
              </Field>
            </ToggleRow>
            <ToggleRow
              label="Electricity billed separately"
              hint="Track a metered electricity line on the ledger."
              on={elecEnabled}
              onToggle={setElecEnabled}
            />
          </Collapsible>
        </>
      )}
    </Sheet>
  );
}
